import { useEffect, useState, type FormEvent } from "react";
import { AlertTriangle, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { License, Product, PromoteLicenseRequest } from "@/lib/types";
import { parseJsonObject } from "@/lib/utils";
import { BusyLabel, InlineAlert } from "@/components/admin/feedback";
import { LicenseConfigurationFields } from "@/components/admin/license-configuration-fields";
import { configurationDraftIsValid } from "@/components/admin/configuration-validation";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DateTimePicker } from "@/components/ui/date-time-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";

export type PromoteLicensePayload = PromoteLicenseRequest;

export function isPromotableTrial(license: License, allowed: boolean) {
  return allowed && license.kind === "trial" && (license.status === "active" || license.status === "expired");
}

export function validatePromotionDraft({
  model,
  validUntil,
  reason,
  confirmed,
}: {
  model: PromoteLicenseRequest["model"];
  validUntil: string;
  reason: string;
  confirmed: boolean;
}) {
  if (!reason.trim()) return "reason" as const;
  if (!confirmed) return "confirmation" as const;
  if (model === "time_limited" && !validUntil) return "expiry" as const;
  return undefined;
}

export function createPromotionIdempotencyKey() {
  return crypto.randomUUID();
}

export function PromoteLicenseDialog({
  open,
  onOpenChange,
  license,
  product,
  busy,
  error,
  onPromote,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  license: License;
  product: Product;
  busy: boolean;
  error: string | null;
  onPromote: (payload: PromoteLicensePayload, idempotencyKey: string) => void;
}) {
  const { t } = useTranslation();
  const [model, setModel] = useState<PromoteLicenseRequest["model"]>("node_locked");
  const [validUntil, setValidUntil] = useState("");
  const [entitlements, setEntitlements] = useState("{}");
  const [policy, setPolicy] = useState('{"max_devices":1}');
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [emptyEntitlementsConfirmed, setEmptyEntitlementsConfirmed] = useState(false);
  const [validation, setValidation] = useState<string | null>(null);
  const [idempotencyKey, setIdempotencyKey] = useState("");
  const evidenceLabels = {
    kind: t("licenses.kind", "Kind"),
    model: t("licenses.model", "Model"),
    validity: t("dialogs.evidenceValidity", "Validity"),
    entitlements: t("dialogs.evidenceEntitlements", "Entitlements"),
    policy: t("licenses.policy", "Policy"),
    perpetual: t("common.perpetual", "Perpetual"),
    invalid: t("dialogs.evidenceInvalidConfiguration", "Configuration JSON is invalid"),
  };
  const beforeEvidence = grantEvidence(license.kind, license.model, license.valid_until ?? null, license.entitlements, license.policy as unknown as Record<string, unknown>, evidenceLabels);
  const afterEvidence = grantEvidence("paid", model, model === "perpetual" ? null : validUntil || null, entitlements, policy, evidenceLabels);

  useEffect(() => {
    if (!open) return;
    setModel("node_locked");
    setValidUntil("");
    setEntitlements("{}");
    setPolicy('{"max_devices":1}');
    setReason("");
    setConfirmed(false);
    setEmptyEntitlementsConfirmed(false);
    setValidation(null);
    setIdempotencyKey(createPromotionIdempotencyKey());
  }, [license.id, open]);

  function close(nextOpen: boolean) {
    if (!busy) onOpenChange(nextOpen);
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!configurationDraftIsValid(event.currentTarget)) return;
    const invalid = validatePromotionDraft({ model, validUntil, reason, confirmed });
    if (invalid === "reason") {
      setValidation(t("dialogs.promoteReasonRequired", "A reason is required to promote this Trial License."));
      requestAnimationFrame(() => document.getElementById("promote-license-reason")?.focus());
      return;
    }
    if (invalid === "confirmation") {
      setValidation(t("dialogs.promoteConfirmationRequired", "Confirm that this one-way replacement is irreversible."));
      requestAnimationFrame(() => document.getElementById("promote-license-confirm")?.focus());
      return;
    }
    if (invalid === "expiry") {
      setValidation(t("dialogs.promoteExpiryRequired", "A Paid time-limited License requires an expiry."));
      requestAnimationFrame(() => document.getElementById("promote-license-valid-until")?.focus());
      return;
    }
    try {
      const configuration = {
        entitlements: parseJsonObject(entitlements),
        policy: parseJsonObject(policy),
        reason: reason.trim(),
      };
      const hasEmptyEntitlements = Object.keys(configuration.entitlements).length === 0;
      if (hasEmptyEntitlements && !emptyEntitlementsConfirmed) {
        setValidation(t("dialogs.promoteEmptyEntitlementsRequired", "Confirm that the Paid License will have no Entitlements."));
        requestAnimationFrame(() => document.getElementById("promote-license-empty-entitlements-confirm")?.focus());
        return;
      }
      const emptyEntitlementsAcknowledgement = hasEmptyEntitlements ? { confirm_empty_entitlements: true } : {};
      const payload: PromoteLicenseRequest = model === "time_limited"
        ? { ...configuration, ...emptyEntitlementsAcknowledgement, model, valid_until: validUntil }
        : model === "perpetual"
          ? { ...configuration, ...emptyEntitlementsAcknowledgement, model, valid_until: null }
          : { ...configuration, ...emptyEntitlementsAcknowledgement, model, valid_until: validUntil || null };
      onPromote(payload, idempotencyKey);
      setValidation(null);
    } catch (value) {
      setValidation(value instanceof Error ? value.message : t("licenses.jsonValidationError", "Entitlements and policy must be valid JSON objects."));
    }
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-3xl">
        <form onSubmit={submit}>
          <DialogHeader>
            <div className="flex items-center gap-2">
              <AlertTriangle className="size-5 text-warning" aria-hidden="true" />
              <DialogTitle>{t("dialogs.promoteLicenseTitle", "Promote Trial License to Paid")}</DialogTitle>
            </div>
            <DialogDescription>
              {t("dialogs.promoteLicenseDescription", "Replace the entire commercial configuration. License ID, Product Key, Activation, and Activation Secret stay the same; clients refresh to receive a Paid token.")}
            </DialogDescription>
          </DialogHeader>

          <section className="mt-4 grid gap-3 border-y border-line py-4 sm:grid-cols-[1fr_auto_1fr]" aria-label={t("dialogs.promoteEvidence", "Promotion evidence")}>
            <Evidence label={t("dialogs.before", "Before")} value={beforeEvidence} />
            <ArrowRight className="hidden self-center text-muted sm:block" aria-hidden="true" />
            <Evidence label={t("dialogs.after", "After")} value={afterEvidence} />
          </section>

          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="promote-license-model">{t("licenses.paidModel", "Paid model")}</Label>
              <Select id="promote-license-model" value={model} onChange={(event) => setModel(event.target.value as PromoteLicenseRequest["model"])} disabled={busy}>
                <option value="node_locked">Node locked</option>
                <option value="time_limited">Time limited</option>
                <option value="perpetual">Perpetual</option>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="promote-license-valid-until">{t("licenses.paidExpiresAt", "Paid expiry")}</Label>
              <DateTimePicker id="promote-license-valid-until" value={validUntil} onValueChange={setValidUntil} disabled={busy || model === "perpetual"} placeholder={model === "perpetual" ? t("licenses.expiryPlaceholderForbidden", "No expiry for Perpetual") : t("licenses.expiryPlaceholderOptional", "Set expiry (optional)")} />
            </div>
          </div>

          <div className="mt-5 border-t border-line pt-5">
            <LicenseConfigurationFields product={product} entitlements={entitlements} policy={policy} disabled={busy} onEntitlementsChange={(value) => { setEntitlements(value); setEmptyEntitlementsConfirmed(false); }} onPolicyChange={setPolicy} />
          </div>

          <div className="mt-5 space-y-3 border-t border-line pt-5">
            <div className="space-y-2">
              <Label htmlFor="promote-license-reason">{t("dialogs.reason", "Reason")} <span className="text-danger">*</span></Label>
              <Input id="promote-license-reason" value={reason} onChange={(event) => setReason(event.target.value)} disabled={busy} required />
            </div>
            <label className="flex items-start gap-2 text-sm text-ink" htmlFor="promote-license-confirm">
              <input id="promote-license-confirm" className="mt-0.5 size-4 accent-ink" type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} disabled={busy} />
              <span>{t("dialogs.promoteIrreversible", "I understand this permanently replaces the Trial configuration with the Paid configuration and cannot be undone.")}</span>
            </label>
            {isEmptyEntitlements(entitlements) && <label className="flex items-start gap-2 text-sm text-warning" htmlFor="promote-license-empty-entitlements-confirm">
              <input id="promote-license-empty-entitlements-confirm" className="mt-0.5 size-4 accent-ink" type="checkbox" checked={emptyEntitlementsConfirmed} onChange={(event) => setEmptyEntitlementsConfirmed(event.target.checked)} disabled={busy} />
              <span>{t("dialogs.promoteEmptyEntitlementsConfirm", "I understand that this Paid License will have no Entitlements and protected features will remain unavailable.")}</span>
            </label>}
            {(validation || error) && <InlineAlert title={t("dialogs.promoteFailed", "Trial promotion could not complete")} message={validation || error!} variant="error" />}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => close(false)}>{t("common.cancel", "Cancel")}</Button>
            <Button type="submit" variant="primary" size="sm" disabled={busy || !reason.trim() || !confirmed}>{busy ? <BusyLabel>{t("dialogs.promoting", "Promoting")}</BusyLabel> : t("dialogs.promoteLicense", "Promote to Paid")}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function grantEvidence(kind: string | undefined, model: string, validUntil: string | null, rawEntitlements: Record<string, unknown> | string, rawPolicy: Record<string, unknown> | string, labels: { kind: string; model: string; validity: string; entitlements: string; policy: string; perpetual: string; invalid: string }) {
  const read = (value: Record<string, unknown> | string) => typeof value === "string" ? parseJsonObject(value) : value;
  try { const entitlements = Object.keys(read(rawEntitlements)); const policy = read(rawPolicy); return [{ label: labels.kind, value: kind ?? "paid" }, { label: labels.model, value: model }, { label: labels.validity, value: validUntil ?? labels.perpetual }, { label: labels.entitlements, value: `${entitlements.length} · ${entitlements.join(", ") || "—"}` }, { label: labels.policy, value: `max_devices: ${String(policy.max_devices ?? "—")}` }]; }
  catch { return [{ label: labels.kind, value: kind ?? "paid" }, { label: labels.model, value: model }, { label: labels.validity, value: validUntil ?? labels.perpetual }, { label: labels.entitlements, value: labels.invalid }, { label: labels.policy, value: labels.invalid }]; }
}

function Evidence({ label, value }: { label: string; value: Array<{ label: string; value: string }> }) {
  return <div className="min-w-0"><p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted">{label}</p><dl className="mt-1 text-xs">{value.map((item) => <div key={item.label} className="grid grid-cols-[minmax(0,6rem)_minmax(0,1fr)] gap-2 border-t border-line py-1.5 first:border-t-0"><dt className="text-muted">{item.label}</dt><dd className="min-w-0 wrap-break-word font-mono text-ink">{item.value}</dd></div>)}</dl></div>;
}

function isEmptyEntitlements(value: string) {
  try { return Object.keys(parseJsonObject(value)).length === 0; }
  catch { return false; }
}
